/**
 * Primitiva de rutas por locale: el prefijo y la resolución de un segmento
 * localizado a href.
 *
 * Vive aparte de `lib/locale-paths.ts` para que las familias de landings
 * (clásicas ahí, catálogo genérico en `lib/generic-landings/paths.ts`) puedan
 * declarar sus segmentos sin importarse entre sí. Únicamente importa el
 * contrato de idiomas del dominio: es la base del grafo.
 */

import { normalizePreferredLanguage, PREFERRED_LANGUAGES, type PreferredLanguage } from '@/server/domain/language'

/**
 * Lista canónica de idiomas públicos, derivada del contrato de dominio
 * (`PREFERRED_LANGUAGES`): router, validación de API, contexto de auth y UI
 * comparten la misma tupla. Añadir un nuevo idioma empieza en
 * `server/domain/language.ts`, y todos los `Record<PublicLocale,…>` dejan de
 * compilar hasta completar el nuevo mercado.
 */
export const PUBLIC_LOCALES = PREFERRED_LANGUAGES

export type PublicLocale = PreferredLanguage

const PREFIX_BY_LOCALE: Record<PublicLocale, string> = {
  es: '',
  en: '/en',
  pt: '/pt',
  it: '/it',
  fr: '/fr',
  ja: '/ja',
  de: '/de',
}

/**
 * Normaliza un segmento de locale arbitrario (JSON, params, storage) al
 * contrato público, con es por defecto. Es un reenvío del normalizador
 * canónico del dominio — no una segunda implementación.
 */
export function toPublicLocale(locale: unknown): PublicLocale {
  return normalizePreferredLanguage(locale)
}

/**
 * ¿Es el valor uno de los idiomas públicos? Guard exacto, SIN normalizar: donde
 * hay que rechazar un locale fuera de contrato (params de una ruta), `toPublicLocale`
 * lo colaría como es y publicaría una URL duplicada.
 */
export function isPublicLocale(value: unknown): value is PublicLocale {
  return typeof value === 'string' && (PUBLIC_LOCALES as readonly string[]).includes(value)
}

/** Prefijo de los locales no-default; vacío en es (`localePrefix: 'as-needed'`). */
export function getLocalePrefix(locale: string): string {
  return PREFIX_BY_LOCALE[toPublicLocale(locale)]
}

/** Etiqueta BCP-47 / Open Graph de cada PublicLocale. Fuente única del mapa. */
export const OG_LOCALE: Record<PublicLocale, string> = {
  es: 'es_ES',
  en: 'en_US',
  pt: 'pt_BR',
  it: 'it_IT',
  fr: 'fr_FR',
  ja: 'ja_JP',
  de: 'de_DE',
}

/**
 * Mapa completo `alternates.languages` a partir de un constructor de href por
 * locale, con `x-default` apuntando a es (mercado principal). Fuente única del
 * hreflang: ninguna página debe volver a declarar la lista de locales a mano —
 * añadir idioma aquí propaga todos los alternates sin tocarlos uno a uno.
 */
export function hreflangFor(
  hrefFor: (locale: PublicLocale) => string,
): Record<PublicLocale | 'x-default', string> {
  const languages = Object.fromEntries(
    PUBLIC_LOCALES.map((locale) => [locale, hrefFor(locale)]),
  ) as Record<PublicLocale, string>
  return { ...languages, 'x-default': hrefFor('es') }
}

/**
 * Href de una landing raíz a partir de su mapa de segmentos por locale.
 * Un locale desconocido cae al segmento de es (mercado principal).
 */
export function hrefForSegment(byLocale: Record<PublicLocale, string>, locale: string): string {
  const publicLocale = toPublicLocale(locale)
  const prefix = getLocalePrefix(publicLocale)
  const path = byLocale[publicLocale]
  return prefix ? `${prefix}/${path}` : `/${path}`
}
